import React from 'react';
import useFirebaseAuth from './useFirebaseAuth';

/**
 * Generic Authentication Component
 * Can be customized for different contexts (user auth, admin auth, etc.)
 */
export const AuthComponent = ({ 
  onAuthChange, 
  context = 'user',
  allowedDomains = null,
  title,
  description,
  benefits = [],
  customStyling = {}
}) => {
  const {
    user,
    loading,
    error,
    initialLoading,
    signInWithGoogle,
    signOut
  } = useFirebaseAuth({ 
    context, 
    allowedDomains 
  });

  // Always call onAuthChange with the real Firebase user object when it changes
  React.useEffect(() => {
    if (onAuthChange) {
      onAuthChange(user || null);
    }
  }, [user, onAuthChange]);

  // Default content based on context
  const defaultContent = {
    user: {
      title: 'Sign in to Save Your Data',
      description: 'Connect with Google to securely store your data and access it from any device.',
      benefits: [
        { icon: '☁️', text: 'Cloud Sync' },
        { icon: '🔒', text: 'Secure Storage' },
        { icon: '📱', text: 'Access Anywhere' }
      ]
    },
    admin: {
      title: 'Admin Sign In',
      description: 'Sign in with your authorized Google account to access the admin dashboard.',
      benefits: [
        { icon: '⚡', text: 'Admin Access' },
        { icon: '🛡️', text: 'Secure Login' },
        { icon: '📊', text: 'Full Control' }
      ]
    },
    'gpa-calculator': {
      title: 'Sign in to Save Your GPA Data',
      description: 'Connect with Google to securely store your GPA calculations and access them from any device.',
      benefits: [
        { icon: '📊', text: 'Save Calculations' },
        { icon: '☁️', text: 'Cloud Backup' },
        { icon: '🔄', text: 'Sync Across Devices' }
      ]
    }
  };

  const content = {
    title: title || defaultContent[context]?.title || defaultContent.user.title,
    description: description || defaultContent[context]?.description || defaultContent.user.description,
    benefits: benefits.length > 0 ? benefits : defaultContent[context]?.benefits || defaultContent.user.benefits
  };

  // Show loading while checking authentication state
  if (initialLoading) {
    return (
      <div className="auth-container loading">
        <div className="auth-loading">
          <div className="loading-spinner"></div>
          <span>Checking authentication...</span>
        </div>
      </div>
    );
  }

  if (user) {
    return (
      <div className="auth-container signed-in" style={customStyling.container}>
        <div className="user-info">
          <img 
            src={user.photoURL || '/default-avatar.png'} 
            alt="Profile" 
            className="user-avatar"
            onError={(e) => {
              e.target.src = '/default-avatar.png';
            }}
          />
          <div className="user-details">
            <span className="user-name">{user.displayName}</span>
            <span className="user-email">{user.email}</span>
            {allowedDomains && (
              <span className="user-role">
                {context === 'admin' ? 'Administrator' : 'Authorized User'}
              </span>
            )}
          </div>
        </div>
        <div className="auth-actions">
          {context === 'admin' && window.location.pathname !== '/admin' && (
            <a href="/admin" className="btn-primary admin-dashboard-btn" style={{ marginRight: '8px', fontSize: '0.85rem', padding: '6px 12px' }}>
              📊 Dashboard
            </a>
          )}
          <button onClick={signOut} className="btn-secondary">
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container" style={customStyling.container}>
      <div className="auth-prompt">
        <div className="auth-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6z" fill="currentColor"/>
          </svg>
        </div>
        <h3>{content.title}</h3>
        <p>{content.description}</p>
        
        {allowedDomains && (
          <div className="domain-restriction">
            <small>Access restricted to: {allowedDomains.join(', ')}</small>
          </div>
        )}
        
        {error && <div className="auth-error">{error}</div>}
        
        <button 
          onClick={signInWithGoogle} 
          disabled={loading}
          className="google-signin-btn"
        >
          {loading ? (
            <div className="loading-spinner"></div>
          ) : (
            <>
              <svg className="google-icon" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </>
          )}
        </button>
        
        {content.benefits.length > 0 && (
          <div className="auth-benefits">
            {content.benefits.map((benefit, index) => (
              <div key={index} className="benefit">
                <span className="benefit-icon">
                  {typeof benefit.icon === 'string' ? benefit.icon : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19.35 10.04C18.67 6.59 15.64 4 12 4C9.11 4 6.6 5.64 5.35 8.04C2.34 8.36 0 10.91 0 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" fill="currentColor"/>
                    </svg>
                  )}
                </span>
                <span>{benefit.text}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthComponent;
